import { useEffect, useState, type FormEvent } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import QRCode from 'qrcode'

import {
    useMfaDisableMutation,
    useMfaRegenerateCodesMutation,
    useMfaSetupMutation,
    useMfaVerifySetupMutation,
    type MfaSetup,
} from '@/apis/authApi'
import { useGetMeQuery } from '@/apis/teamApi'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'
import { CopyButton } from '@/components/shared/CopyButton'
import { setMfaRequired } from '@/modules/auth/authSlice'

const apiMessage = (err: unknown, fallback: string) =>
    (err as { data?: { error?: { message?: string } } })?.data?.error?.message ?? fallback

/** Recovery codes as a text file the user saves (generated in the browser, never uploaded). */
function downloadCodes(email: string, codes: string[]) {
    const text = `YOM recovery codes for ${email}\nEach code works once.\n\n${codes.join('\n')}\n`
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }))
    const a = document.createElement('a')
    a.href = url
    a.download = 'yom-recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
}

function EnableMfa({ email, onEnabled }: { email: string; onEnabled: () => void }) {
    const [setup, { isLoading: starting, error: setupError }] = useMfaSetupMutation()
    const [verifySetup, { isLoading: verifying, error: verifyError }] = useMfaVerifySetupMutation()
    const [data, setData] = useState<MfaSetup | null>(null)
    const [qr, setQr] = useState<string | null>(null)
    const [code, setCode] = useState('')
    const [saved, setSaved] = useState(false)

    useEffect(() => {
        if (data) {
            QRCode.toDataURL(data.uri, { margin: 1, width: 200 })
                .then(setQr)
                .catch(() => setQr(null))
        }
    }, [data])

    if (!data) {
        return (
            <Box>
                {setupError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {apiMessage(setupError, 'Could not start MFA setup')}
                    </Alert>
                )}
                <Button
                    variant="contained"
                    disabled={starting}
                    onClick={async () => setData(await setup().unwrap())}
                >
                    {starting ? 'Starting…' : 'Turn on two-step verification'}
                </Button>
            </Box>
        )
    }

    const confirm = async (e: FormEvent) => {
        e.preventDefault()
        try {
            await verifySetup({ code: code.replace(/\s+/g, '') }).unwrap()
            onEnabled()
        } catch {
            setCode('')
        }
    }

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                    1. Scan this with your authenticator app
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Google Authenticator, 1Password, Authy and similar apps work.
                </Typography>
                <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                    {qr && (
                        <img
                            src={qr}
                            alt="QR code for your authenticator app"
                            width={200}
                            height={200}
                        />
                    )}
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            Can't scan? Enter this key:
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography
                                sx={{ fontFamily: 'monospace', letterSpacing: 1 }}
                                data-testid="mfa-secret"
                            >
                                {data.secret}
                            </Typography>
                            <CopyButton text={data.secret} />
                        </Stack>
                    </Box>
                </Stack>
            </Box>
            <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                    2. Save your recovery codes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    If you lose your phone, each of these lets you sign in once. They won't be shown
                    again.
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, max-content)',
                        columnGap: 4,
                        rowGap: 0.5,
                        fontFamily: 'monospace',
                        p: 2,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        mb: 1,
                    }}
                    data-testid="recovery-codes"
                >
                    {data.recoveryCodes.map(c => (
                        <span key={c}>{c}</span>
                    ))}
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        onClick={() => {
                            downloadCodes(email, data.recoveryCodes)
                            setSaved(true)
                        }}
                    >
                        Download
                    </Button>
                    <Button
                        size="small"
                        onClick={() => {
                            void navigator.clipboard.writeText(data.recoveryCodes.join('\n'))
                            setSaved(true)
                        }}
                    >
                        Copy
                    </Button>
                    <Button size="small" onClick={() => setSaved(true)}>
                        I've saved them
                    </Button>
                </Stack>
            </Box>
            <Box component="form" onSubmit={confirm}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    3. Enter the 6-digit code from the app
                </Typography>
                {verifyError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        That code is not valid. Check the time on your phone and try again.
                    </Alert>
                )}
                <Stack direction="row" spacing={2}>
                    <TextField
                        size="small"
                        label="Code"
                        value={code}
                        onChange={e => setCode(e.target.value)}
                        required
                        autoComplete="one-time-code"
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9 ]{6,7}', maxLength: 7 }}
                    />
                    <Button type="submit" variant="contained" disabled={!saved || verifying}>
                        {verifying ? 'Checking…' : 'Turn on'}
                    </Button>
                </Stack>
                {!saved && (
                    <Typography variant="caption" color="text.secondary">
                        Save your recovery codes first.
                    </Typography>
                )}
            </Box>
        </Stack>
    )
}

function DisableMfa({ onDisabled }: { onDisabled: () => void }) {
    const [disable, { isLoading, error }] = useMfaDisableMutation()
    const [password, setPassword] = useState('')
    const submit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            await disable({ password }).unwrap()
            onDisabled()
        } catch {
            setPassword('')
        }
    }
    return (
        <Box component="form" onSubmit={submit}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                To turn it off, confirm your password. Your recovery codes stop working too.
            </Typography>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {apiMessage(error, 'Could not turn off two-step verification')}
                </Alert>
            )}
            <Stack direction="row" spacing={2}>
                <TextField
                    size="small"
                    type="password"
                    label="Password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                />
                <Button type="submit" color="error" variant="outlined" disabled={isLoading}>
                    Turn off
                </Button>
            </Stack>
        </Box>
    )
}

/** New recovery codes (the old ones stop working); confirmed with a current authenticator code. */
function RegenerateCodes({ email }: { email: string }) {
    const [regenerate, { isLoading, error }] = useMfaRegenerateCodesMutation()
    const [code, setCode] = useState('')
    const [codes, setCodes] = useState<string[] | null>(null)
    const submit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            setCodes((await regenerate({ code: code.replace(/\s+/g, '') }).unwrap()).recoveryCodes)
        } finally {
            setCode('')
        }
    }
    if (codes) {
        return (
            <Alert severity="success" onClose={() => setCodes(null)}>
                New recovery codes. Your old ones no longer work. Save these now; they won't be
                shown again.
                <Box sx={{ fontFamily: 'monospace', my: 1 }} data-testid="new-recovery-codes">
                    {codes.join('  ')}
                </Box>
                <Button size="small" onClick={() => downloadCodes(email, codes)}>
                    Download
                </Button>
            </Alert>
        )
    }
    return (
        <Box component="form" onSubmit={e => submit(e).catch(() => undefined)}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Lost your recovery codes or used most of them? Get a new set. Confirm with a code
                from your app.
            </Typography>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    That code is not valid.
                </Alert>
            )}
            <Stack direction="row" spacing={2}>
                <TextField
                    size="small"
                    label="Authenticator code"
                    value={code}
                    onChange={e => setCode(e.target.value)}
                    required
                    autoComplete="one-time-code"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9 ]{6,7}', maxLength: 7 }}
                />
                <Button type="submit" variant="outlined" disabled={isLoading}>
                    New recovery codes
                </Button>
            </Stack>
        </Box>
    )
}

/** Account → Security: the signed-in user's own two-step verification (TOTP + recovery codes). */
export function SecurityPage() {
    const dispatch = useAppDispatch()
    const { data: me, refetch } = useGetMeQuery()
    const orgRequiresMfa = useAppSelector(s => s.auth.mfaRequired)
    const notify = (message: string) => dispatch(showNotification({ message, severity: 'success' }))

    return (
        <Box sx={{ p: 3, maxWidth: 800 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
                Security
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Sign-in settings for {me?.email ?? 'your account'}. They apply to every organization
                you belong to.
            </Typography>
            {orgRequiresMfa && !me?.mfaEnabled && (
                <Alert severity="warning" sx={{ mb: 3 }} data-testid="mfa-required">
                    Your organization requires two-step verification. Turn it on below to keep using
                    the dashboard.
                </Alert>
            )}
            <Card>
                <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <Typography variant="h6">Two-step verification</Typography>
                        {me && (
                            <Chip
                                size="small"
                                label={me.mfaEnabled ? 'On' : 'Off'}
                                color={me.mfaEnabled ? 'success' : 'default'}
                                data-testid="mfa-status"
                            />
                        )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        After your password (or Google sign-in), you'll also enter a code from an
                        authenticator app.
                    </Typography>
                    {me &&
                        (me.mfaEnabled ? (
                            <Stack spacing={3}>
                                <RegenerateCodes email={me.email} />
                                <DisableMfa
                                    onDisabled={() => {
                                        notify('Two-step verification is off')
                                        void refetch()
                                    }}
                                />
                            </Stack>
                        ) : (
                            <EnableMfa
                                email={me.email}
                                onEnabled={() => {
                                    notify('Two-step verification is on')
                                    dispatch(setMfaRequired(false))
                                    void refetch()
                                }}
                            />
                        ))}
                </CardContent>
            </Card>
        </Box>
    )
}
